import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { initAnalyticsListeners } from '@/features/analytics';
import { RootErrorBoundary } from '@/components/error/RootErrorBoundary';
import { FullScreenLoader } from '@/components/loading/FullScreenLoader';
import { SkipLink } from '@/components/layout/SkipLink';
import { MarketingHeader } from '@/components/layout/MarketingHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CookieBanner } from '@/components/layout/CookieBanner';
import { OnboardingTour } from '@/features/onboarding';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { FeedbackButton } from '@/features/feedback';
import { useUrlSync } from '@/hooks/useUrlSync';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import { isLangPrefixedPath, stripLangPrefix } from '@/i18n/paths';
import { useShortlistSync } from '@/features/shortlist';
import { useSyncQuizOnLogin } from '@/features/quiz';
import { countriesQuery, queries } from '@/data';
import { setActiveMapCountry } from '@/data/active-country';
import {
  DEFAULT_MAP_COUNTRY,
  isMapCountry,
  type MapCountry,
} from '@/features/map-core/countries';
import { useMapStore } from '@/features/map-core/store';
import { useCompareStore } from '@/features/compare/store';
import { emit } from '@/lib/events';
import { uiLang } from '@/i18n/lang';

/** Canonical host — must match the origin build-sitemap.mjs advertises. */
const PROD_ORIGIN = 'https://lodestar.place';

const MapPage = lazy(() => import('./pages/MapPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const WorldMapPage = lazy(() => import('./pages/WorldMapPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const QuizResultPage = lazy(() => import('./pages/QuizResultPage'));
const ReportPage = lazy(() => import('./pages/ReportPage'));
const ReportPreviewPage = lazy(() => import('./pages/ReportPreviewPage'));
const CountriesPage = lazy(() => import('./pages/CountriesPage'));
const CountryHubPage = lazy(() => import('./pages/CountryHubPage'));
const CountryTopicPage = lazy(() => import('./pages/CountryTopicPage'));
const TopicLandingPage = lazy(() => import('./pages/TopicLandingPage'));
const TopicsIndexPage = lazy(() => import('./pages/TopicsIndexPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const GuideArticlePage = lazy(() => import('./pages/GuideArticlePage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const NextStepsPage = lazy(() => import('./pages/NextStepsPage'));
const RecommendationsPage = lazy(() => import('./pages/RecommendationsPage'));
const StoryPage = lazy(() => import('./pages/StoryPage'));
const ConsultantPage = lazy(() => import('./pages/ConsultantPage'));
// CV.PoL — career-vector holding page. Lazy-loaded; ships in initial bundle
// only via the 4-pillar section's <Link to="/career">. M0 surface.
const CareerHoldingPage = lazy(() => import('./pages/CareerHoldingPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const RefundPage = lazy(() => import('./pages/RefundPage'));
const ReportLandingPage = lazy(() => import('./pages/ReportLandingPage'));
// F8 — Partner programme. Lazy-loaded so the partner intake surface only
// ships when a prospective consultant visits it.
const PartnersLandingPage = lazy(() => import('./pages/PartnersLandingPage'));
const PartnersApplyPage = lazy(() => import('./pages/PartnersApplyPage'));
const PartnersTermsPage = lazy(() => import('./pages/PartnersTermsPage'));
const PartnersDpaPage = lazy(() => import('./pages/PartnersDpaPage'));
const PartnersFaqPage = lazy(() => import('./pages/PartnersFaqPage'));
// Admin dashboard — local-only operator surface. Gated by a pre-shared key
// (VITE_ADMIN_KEY), not by the regular auth system. Lazy so it never ships
// in the initial public bundle.
const AdminPage = lazy(() => import('./pages/AdminPage'));


function OfflineBanner() {
  const online = useOnlineStatus();
  const { t } = useTranslation();
  if (online) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-amber-100 px-4 py-1.5 text-center text-xs text-amber-900"
    >
      {t('offline.banner')}
    </div>
  );
}

function CompareBar() {
  const { t } = useTranslation();
  const { istats, clear } = useCompareStore();
  if (istats.length === 0) return null;
  const url = `/it/compare?ids=${istats.join(',')}`;
  const clearLabel = t('compare.clearAll', { defaultValue: 'Clear compare' });
  return (
    <div
      role="region"
      aria-label={t('compare.barAriaLabel', { defaultValue: 'Compare queue' })}
      className="fixed bottom-4 left-1/2 z-[1100] -translate-x-1/2 flex items-center gap-2 rounded-full border border-[color:var(--color-ink-100)] bg-[color:var(--color-paper)] px-4 py-2 shadow-lg"
    >
      <span aria-live="polite" className="text-sm font-medium text-[color:var(--color-ink-700)]">
        {t('compare.queued', { count: istats.length, defaultValue: '{{count}} selected' })}
      </span>
      <Link
        to={url}
        onClick={() => emit('compare:opened', { count: istats.length })}
        className="rounded-full bg-[color:var(--color-terra-600)] px-3 py-1 text-xs font-semibold text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-terra-600)]"
      >
        {t('compare.go', { defaultValue: 'Compare →' })}
      </Link>
      <button
        type="button"
        onClick={clear}
        aria-label={clearLabel}
        title={clearLabel}
        className="text-[color:var(--color-ink-400)] hover:text-[color:var(--color-ink-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-terra-600)]"
      >
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );
}

// Guard мультистранного роута /:cc/map. Неизвестный код страны — не пустая
// карта и не молчаливый редирект на Италию, а общий fallback (404-поведение
// приложения): иначе /xx/map выглядел бы как «страна есть, данных нет».
function CountryMapRoute() {
  const { cc } = useParams<{ cc: string }>();
  if (!isMapCountry(cc)) return <FallbackRedirect />;
  return <MapWithHeader key={cc} cc={cc} />;
}

/**
 * <head> for `/<cc>/map`. Driven by the route's country code, so every entry
 * in MAP_COUNTRIES gets its own title/description/canonical the moment it is
 * added — the sitemap derives that list from the same registry.
 *
 * `/<cc>/map` renders bilingual at a single URL (build-sitemap.mjs lists it
 * without alternates), so there is no hreflang set here — only a
 * self-referential canonical. Before this the prerendered snapshot inherited
 * the SPA shell's <title> and had no canonical at all.
 */
function useMapRouteSeo(cc: MapCountry) {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'ru' = uiLang(i18n.language);
  const { data: countriesFile } = useQuery(countriesQuery());
  const entry = countriesFile?.countries.find((c) => c.code === cc.toUpperCase());
  const country = (lang === 'ru' ? entry?.name_ru : entry?.name_en) ?? cc.toUpperCase();
  const canonical = `${PROD_ORIGIN}/${cc}/map`;

  useSeoMeta({
    title: t('map.meta.title', { country }),
    description: t('map.meta.description', { country }),
    canonical,
    locale: lang === 'ru' ? 'ru_RU' : 'en_US',
  });
  useJsonLd(
    JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: t('app.title'), item: `${PROD_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: country, item: canonical },
      ],
    }),
    'map-breadcrumb',
  );
}

function MapWithHeader({ cc = DEFAULT_MAP_COUNTRY }: { cc?: MapCountry }) {
  // Активная страна выставляется СИНХРОННО, до useUrlSync и до любого запроса
  // данных: от неё зависят и пути файлов, и query-ключи. useState с ленивой
  // инициализацией гарантирует, что это произойдёт в первом же рендере.
  useState(() => {
    setActiveMapCountry(cc);
    useMapStore.getState().hydrateCountry(cc);
    return cc;
  });

  // <head> живёт здесь, а не в MapPage: MapPage монтируется только после
  // загрузки meta+light, а пререндер снимает HTML сразу после networkidle —
  // страница обязана иметь title/canonical даже на экране загрузки.
  useMapRouteSeo(cc);

  // URL sync ставится ПЕРВЫМ чтобы applyUrlState отработал до рендера features
  useUrlSync();
  useDocumentLang();
  useShortlistSync();

  // Pre-fetch meta + light в App-уровне, чтобы FullScreenLoader корректно ждал boot
  const { data: meta } = useQuery(queries.meta());
  useQuery({ ...queries.light(meta?.version ?? ''), enabled: !!meta?.version });

  // Стартовый вид из meta.json страны — но только если его не задали URL
  // или сохранённый вьюпорт этой же страны (приоритет: URL > persist > meta).
  useEffect(() => {
    if (meta) useMapStore.getState().applyMetaDefaults(meta.center, meta.zoom);
  }, [meta]);

  const fetchingMeta = useIsFetching({ queryKey: ['meta', cc] });
  const fetchingLight = useIsFetching({ queryKey: ['units', cc, 'light'] });
  const isBootstrapping = fetchingMeta + fetchingLight > 0 && !meta;

  return (
    <div className="flex h-full w-full flex-col">
      <SkipLink />
      <MarketingHeader withHelpMenu withShare withShortlist blurOnScroll={false} />
      <OfflineBanner />
      <main id="main-content" className="min-h-0 flex-1 overflow-hidden">
        {isBootstrapping ? (
          <FullScreenLoader />
        ) : (
          <>
            <MapPage cc={cc} />
            <OnboardingTour />
            <CompareBar />
          </>
        )}
      </main>
    </div>
  );
}

function LoginWithHeader() {
  useDocumentLang();
  return (
    <div className="flex h-full w-full flex-col">
      <MarketingHeader />
      <main className="flex-1 overflow-auto">
        <LoginPage />
      </main>
    </div>
  );
}

// Resets the SimplePage scroll container to top on every pathname change.
// window.scrollTo() is a no-op here because the real scroll container is
// <main id="main-content" className="overflow-auto">, not window.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.getElementById('main-content')?.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);
  return null;
}

// Generic wrapper for static/utility pages (compare, account, privacy, terms)
function SimplePage({ children }: { children: React.ReactNode }) {
  useDocumentLang();
  return (
    <div className="flex h-full w-full flex-col">
      <ScrollToTop />
      <MarketingHeader />
      <main id="main-content" className="flex-1 overflow-auto">
        {children}
        <SiteFooter />
      </main>
    </div>
  );
}

// Root `/` теперь = marketing landing. Legacy map URL hashes (`#l=…&f=…`,
// `#p=…`) форвардим на карту Италии, чтобы старые ссылки не ломались.
// In-page anchors (`#dimensions`, `#faq`, `#methodology`) пропускаем дальше.
function RootRoute() {
  const location = useLocation();
  if (location.hash && /^#[a-z]=/i.test(location.hash)) {
    return <Navigate to={{ pathname: '/it/map', hash: location.hash }} replace />;
  }
  return <HomePage />;
}

// Catch-all → quiz landing (без hash forwarding на /it/map, чтобы случайные ссылки не пробрасывали state).
function FallbackRedirect() {
  return <Navigate to="/" replace />;
}

// Mounted once at the App root inside the Router context. Syncs the
// in-progress quiz across the anon → registered boundary so a user who
// finishes the quiz, signs in via magic-link, and lands back on the SPA
// is redirected to /quiz/result with their answers intact.
function QuizSyncGate() {
  useSyncQuizOnLogin();
  return null;
}

// URL is the source of truth for language on every route family that has a
// `/<lang>` twin (countries, topics, guide, report, refund, map). Landing on
// `/ru/topics` with an English preference used to render the English UI at a
// Russian URL — and vice versa. Page components that take a `lang` prop do
// this locally; this gate covers the ones that don't (atlas, world map,
// report landing, refund) so every prefixed URL renders in its own language.
function LangUrlSync() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isLangPrefixedPath(pathname)) return;
    const { lang } = stripLangPrefix(pathname);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [pathname, i18n]);
  return null;
}

export default function App() {
  useEffect(() => {
    return initAnalyticsListeners();
  }, []);

  return (
    <RootErrorBoundary>
      <CookieBanner />
      <FeedbackButton />
      <QuizSyncGate />
      <LangUrlSync />
      <Suspense fallback={<FullScreenLoader />}>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/quiz/result" element={<QuizResultPage />} />
          {/* Public product page for the paid report — always visible,
              indexable, links all policies. EN /report, RU /ru/report. */}
          <Route path="/report" element={<SimplePage><ReportLandingPage /></SimplePage>} />
          <Route path="/ru/report" element={<SimplePage><ReportLandingPage /></SimplePage>} />
          {/* Free locked preview of the report — built from the ?a=<blob>
              answers snapshot. noindex. Owns its own header. */}
          <Route path="/report/preview" element={<ReportPreviewPage lang="en" />} />
          <Route path="/ru/report/preview" element={<ReportPreviewPage lang="ru" />} />
          {/* Purchased relocation report — token is the bearer credential.
              EN at /report/:token, RU at /ru/report/:token. noindex. */}
          <Route path="/report/:token" element={<ReportPage />} />
          <Route path="/ru/report/:token" element={<ReportPage />} />
          {/* Refund policy — required by the payment provider; linked from
              the footer + product page + terms. */}
          <Route path="/refund" element={<SimplePage><RefundPage /></SimplePage>} />
          <Route path="/ru/refund" element={<SimplePage><RefundPage /></SimplePage>} />
          <Route path="/countries" element={<SimplePage><CountriesPage /></SimplePage>} />

          {/* Quiet Atlas world map — single page hosting the world view.
              EN at /map, RU at /ru/map. The page itself owns its layout
              (its own MarketingHeader instance), so wrap it without the
              auth-app shell. */}
          <Route path="/map" element={<WorldMapPage />} />
          <Route path="/ru/map" element={<WorldMapPage />} />

          {/* Country wiki — EN (default lang, no prefix) */}
          <Route
            path="/countries/:slug"
            element={<SimplePage><CountryHubPage lang="en" /></SimplePage>}
          />
          <Route
            path="/countries/:slug/:topic"
            element={<SimplePage><CountryTopicPage lang="en" /></SimplePage>}
          />

          {/* Topics index — all dimensions grid (EN) */}
          <Route
            path="/topics"
            element={<SimplePage><TopicsIndexPage lang="en" /></SimplePage>}
          />
          {/* Topic landing pages — cross-country index per dimension (EN) */}
          <Route
            path="/topics/:slug"
            element={<SimplePage><TopicLandingPage lang="en" /></SimplePage>}
          />

          {/* Field Guide — map usage documentation (EN, no prefix) */}
          <Route
            path="/guide"
            element={<SimplePage><GuidePage lang="en" /></SimplePage>}
          />
          <Route
            path="/guide/:section"
            element={<SimplePage><GuidePage lang="en" /></SimplePage>}
          />
          <Route
            path="/guide/:section/:article"
            element={<SimplePage><GuideArticlePage lang="en" /></SimplePage>}
          />

          {/* Field Guide — RU (path prefix) */}
          <Route
            path="/ru/guide"
            element={<SimplePage><GuidePage lang="ru" /></SimplePage>}
          />
          <Route
            path="/ru/guide/:section"
            element={<SimplePage><GuidePage lang="ru" /></SimplePage>}
          />
          <Route
            path="/ru/guide/:section/:article"
            element={<SimplePage><GuideArticlePage lang="ru" /></SimplePage>}
          />

          {/* Country wiki — RU (path prefix) */}
          <Route
            path="/ru/countries"
            element={<SimplePage><CountriesPage /></SimplePage>}
          />
          <Route
            path="/ru/countries/:slug"
            element={<SimplePage><CountryHubPage lang="ru" /></SimplePage>}
          />
          <Route
            path="/ru/countries/:slug/:topic"
            element={<SimplePage><CountryTopicPage lang="ru" /></SimplePage>}
          />

          {/* Topics index — all dimensions grid (RU) */}
          <Route
            path="/ru/topics"
            element={<SimplePage><TopicsIndexPage lang="ru" /></SimplePage>}
          />
          {/* Topic landing pages — RU (path prefix) */}
          <Route
            path="/ru/topics/:slug"
            element={<SimplePage><TopicLandingPage lang="ru" /></SimplePage>}
          />

          <Route path="/it" element={<Navigate to="/it/map" replace />} />
          <Route path="/it/map" element={<MapWithHeader cc="it" />} />
          {/* Мультистранный роут. Статические сегменты (/map, /ru/map — мировая
              карта, где ru это ЯЗЫК, а не страна) ранжируются react-router выше
              динамического, поэтому коллизии нет. Guard отсекает всё, чего нет
              в MAP_COUNTRIES: неизвестный код → 404, а не пустая карта. */}
          <Route path="/:cc/map" element={<CountryMapRoute />} />
          <Route path="/it/compare" element={<SimplePage><ComparePage /></SimplePage>} />
          <Route path="/it/next-steps" element={<SimplePage><NextStepsPage /></SimplePage>} />
          <Route path="/checklists/:slug" element={<SimplePage><NextStepsPage /></SimplePage>} />
          <Route path="/it/recommendations" element={<SimplePage><RecommendationsPage /></SimplePage>} />
          <Route path="/share-your-story" element={<SimplePage><StoryPage /></SimplePage>} />
          <Route path="/consultant" element={<SimplePage><ConsultantPage /></SimplePage>} />
          {/* CV.PoL — career-vector holding page (M0). Country-agnostic; serves
              EN + RU based on i18n state, no path-prefix RU variant for now
              (M0 keeps URL surface minimal — single canonical /career). */}
          <Route path="/career" element={<SimplePage><CareerHoldingPage /></SimplePage>} />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <SimplePage>
                  <AccountPage />
                </SimplePage>
              </RequireAuth>
            }
          />
          <Route path="/privacy" element={<SimplePage><PrivacyPage /></SimplePage>} />
          <Route path="/terms" element={<SimplePage><TermsPage /></SimplePage>} />
          <Route path="/partners" element={<SimplePage><PartnersLandingPage /></SimplePage>} />
          <Route path="/partners/apply" element={<SimplePage><PartnersApplyPage /></SimplePage>} />
          <Route path="/partners/terms" element={<SimplePage><PartnersTermsPage /></SimplePage>} />
          <Route path="/partners/dpa" element={<SimplePage><PartnersDpaPage /></SimplePage>} />
          <Route path="/partners/faq" element={<SimplePage><PartnersFaqPage /></SimplePage>} />
          <Route path="/login" element={<LoginWithHeader />} />
          {/* Admin dashboard — bare route, no Header wrapper, pre-shared key gate. */}
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<FallbackRedirect />} />
        </Routes>
      </Suspense>
    </RootErrorBoundary>
  );
}
